import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { updateError } from './user';
import { callAdminApi, callApi } from '../../utils/apiCall';

const initialState = {
    isLoading: false,
    users: { results: [], count: 0 },
    selectedUser: null,
    practices: [],
    filters: {
        email: '',
        name: '',
        practice: '',
        role: '',
        is_enabled: '',
        is_active: '',
        page: 1,
        pageSize: 25
    },
    actionLoadingCount: 0
};

export const slice = createSlice({
    name: 'superAdminUsers',
    initialState,
    reducers: {
        setIsLoading(state, action) {
            state.isLoading = action.payload;
        },
        setUsers(state, action) {
            state.users = action.payload;
        },
        setSelectedUser(state, action) {
            state.selectedUser = action.payload;
        },
        setPractices(state, action) {
            state.practices = action.payload;
        },
        setFilters(state, action) {
            state.filters = { ...state.filters, ...action.payload };
        },
        resetFilters(state) {
            state.filters = initialState.filters;
        },
        startAction(state) {
            state.actionLoadingCount += 1;
        },
        endAction(state) {
            state.actionLoadingCount = Math.max(0, state.actionLoadingCount - 1);
        }
    }
});

export const { setUsers, setSelectedUser, setPractices, setFilters, resetFilters, setIsLoading, startAction, endAction } = slice.actions;

export default slice.reducer;

// GET /api/v1/management/users/
export function fetchUsers(filters) {
    return async () => {
        dispatch(slice.actions.setIsLoading(true));
        try {
            const params = new URLSearchParams();
            if (filters?.email) params.append('email', filters.email);
            if (filters?.name) params.append('name', filters.name);
            if (filters?.practice) params.append('practice', filters.practice);
            if (filters?.role) params.append('role', filters.role);
            if (filters?.is_enabled !== '' && filters?.is_enabled !== undefined) params.append('is_enabled', filters.is_enabled);
            if (filters?.is_active !== '' && filters?.is_active !== undefined) params.append('is_active', filters.is_active);
            if (filters?.page) params.append('offset', ((filters.page - 1) * (filters.pageSize || 25)));
            if (filters?.pageSize) params.append('limit', filters.pageSize);
            const query = params.toString();
            const response = await callAdminApi(`/api/v1/management/users/${query ? `?${query}` : ''}`);
            if (response.ok) {
                dispatch(slice.actions.setUsers(response.data));
            } else {
                dispatch(updateError({ success: false, message: response.data?.detail || 'Failed to fetch users' }));
                dispatch(slice.actions.setUsers({ results: [], count: 0 }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to fetch users' }));
            dispatch(slice.actions.setUsers({ results: [], count: 0 }));
        }
        dispatch(slice.actions.setIsLoading(false));
    };
}

// POST /api/v1/management/users/{userId}/disable/
export function disableUser(userId, onSuccess, onComplete) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callAdminApi(`/api/v1/management/users/${userId}/disable/`, 'post');
            if (response.ok) {
                dispatch(updateError({ success: true, message: 'User disabled successfully' }));
                if (onSuccess) onSuccess();
            } else {
                dispatch(updateError({ success: false, message: response.data?.detail || 'Failed to disable user' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to disable user' }));
        }
        dispatch(slice.actions.endAction());
        if (onComplete) onComplete();
    };
}

// POST /api/v1/management/users/{userId}/enable/
export function enableUser(userId, onSuccess, onComplete) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callAdminApi(`/api/v1/management/users/${userId}/enable/`, 'post');
            if (response.ok) {
                dispatch(updateError({ success: true, message: 'User enabled successfully' }));
                if (onSuccess) onSuccess();
            } else {
                dispatch(updateError({ success: false, message: response.data?.detail || 'Failed to enable user' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to enable user' }));
        }
        dispatch(slice.actions.endAction());
        if (onComplete) onComplete();
    };
}

// POST /api/v1/management/users/{userId}/reset_password/
export function resetUserPassword(userId, onSuccess, onComplete) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callAdminApi(`/api/v1/management/users/${userId}/reset_password/`, 'post');
            if (response.ok) {
                dispatch(updateError({ success: true, message: 'Password reset email sent successfully' }));
                if (onSuccess) onSuccess();
            } else {
                dispatch(updateError({ success: false, message: response.data?.detail || 'Failed to send password reset email' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to send password reset email' }));
        }
        dispatch(slice.actions.endAction());
        if (onComplete) onComplete();
    };
}

// POST /api/v1/management/users/{userId}/add_to_practice/
export function addUserToPractice(userId, data, onSuccess, onComplete) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callAdminApi(`/api/v1/management/users/${userId}/add_to_practice/`, 'post', data);
            if (response.ok) {
                dispatch(updateError({ success: true, message: 'User added to practice successfully' }));
                if (onSuccess) onSuccess();
            } else {
                dispatch(updateError({ success: false, message: response.data?.detail || 'Failed to add user to practice' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to add user to practice' }));
        }
        dispatch(slice.actions.endAction());
        if (onComplete) onComplete();
    };
}

// PUT /api/v1/practices/{practiceId}/users/{practiceUserId}/disable/
export function disableFromPractice(practiceId, practiceUserId, onSuccess, onComplete) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/users/${practiceUserId}/disable/`, 'PUT');
            if (response.ok) {
                dispatch(updateError({ success: true, message: 'User disabled from practice successfully' }));
                if (onSuccess) onSuccess();
            } else {
                dispatch(updateError({ success: false, message: response.data?.detail || 'Failed to disable user from practice' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to disable user from practice' }));
        }
        dispatch(slice.actions.endAction());
        if (onComplete) onComplete();
    };
}

// POST /api/v1/management/users/{userId}/remove_from_practice/
export function removeUserFromPractice(userId, data, onSuccess, onComplete) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callAdminApi(`/api/v1/management/users/${userId}/remove_from_practice/`, 'post', data);
            if (response.ok) {
                dispatch(updateError({ success: true, message: 'User removed from practice successfully' }));
                if (onSuccess) onSuccess();
            } else {
                dispatch(updateError({ success: false, message: response.data?.detail || 'Failed to remove user from practice' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to remove user from practice' }));
        }
        dispatch(slice.actions.endAction());
        if (onComplete) onComplete();
    };
}

// PATCH /api/v1/management/users/{userId}/
export function updateUser(userId, data) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callAdminApi(`/api/v1/management/users/${userId}/`, 'patch', data);
            if (response.ok) {
                dispatch(updateError({ success: true, message: 'User updated successfully' }));
                return response.data;
            }
            dispatch(updateError({ success: false, message: response.data?.detail || 'Failed to update user' }));
            return null;
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to update user' }));
            return null;
        } finally {
            dispatch(slice.actions.endAction());
        }
    };
}

// PATCH /api/v1/practices/{practiceId}/users/{practiceUserId}/ — change role on existing practice-user
// onComplete is always called (success or failure) so bulk callers can track completion.
// onSuccess is only called on success.
export function changeUserRole(practiceId, practiceUserId, data, onSuccess, onComplete) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/users/${practiceUserId}/`, 'PATCH', { role: data.role });
            if (response.ok) {
                dispatch(updateError({ success: true, message: 'User role updated successfully' }));
                if (onSuccess) onSuccess();
            } else {
                dispatch(updateError({ success: false, message: response.data?.detail || response.data?.role?.[0] || 'Failed to update role' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to update role' }));
        }
        dispatch(slice.actions.endAction());
        if (onComplete) onComplete();
    };
}

// POST /api/v1/practices/{practiceId}/invitations/ with is_auto_accept: true
export function inviteUserToPractice(practiceId, data, onSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/invitations/`, 'post', {
                practice: practiceId,
                role: data.role,
                email: data.email,
                first_name: data.first_name,
                last_name: data.last_name || '',
                job_title: data.job_title || '',
                is_auto_accept: true,
            });
            if (response.ok) {
                if (onSuccess) onSuccess(response.data);
            } else {
                dispatch(updateError({ success: false, message: response.data?.email?.[0] || response.data?.detail || 'Failed to add user to practice' }));
                if (onSuccess) onSuccess(null);
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to add user to practice' }));
            if (onSuccess) onSuccess(null);
        }
    };
}

// GET /api/v1/management/users/?email=...
export function fetchUserByEmail(email, onSuccess) {
    return async () => {
        try {
            const response = await callAdminApi(`/api/v1/management/users/?email=${encodeURIComponent(email)}&limit=1`);
            if (response.ok) {
                const user = response.data?.results?.[0] || null;
                if (onSuccess) onSuccess(user);
            } else if (onSuccess) {
                onSuccess(null);
            }
        } catch (error) {
            if (onSuccess) onSuccess(null);
        }
    };
}

// PATCH /api/v1/management/users/{userId}/ — upload avatar
export function uploadAvatar(userId, file, onSuccess) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const formData = new FormData();
            formData.append('avatar', file);
            const response = await callAdminApi(`/api/v1/management/users/${userId}/`, 'patch', formData);
            if (response.ok) {
                dispatch(updateError({ success: true, message: 'Avatar updated successfully' }));
                if (onSuccess) onSuccess(response.data);
            } else {
                dispatch(updateError({ success: false, message: response.data?.detail || 'Failed to update avatar' }));
                if (onSuccess) onSuccess(null);
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to update avatar' }));
            if (onSuccess) onSuccess(null);
        }
        dispatch(slice.actions.endAction());
    };
}

// GET /api/v1/management/practices/
export function fetchAllPractices() {
    return async () => {
        try {
            const response = await callAdminApi('/api/v1/management/practices/');
            if (response.ok) {
                dispatch(slice.actions.setPractices(response.data?.results || response.data || []));
            } else {
                console.error('[superAdminUsers] Failed to fetch practices:', response.status);
                dispatch(slice.actions.setPractices([]));
            }
        } catch (error) {
            console.error('[superAdminUsers] API error for practices:', error?.message);
            dispatch(slice.actions.setPractices([]));
        }
    };
}
